import React, { useState, useRef, useCallback, useEffect } from 'react';
import './styles/App.css'; 
import Books from "./books.js"
import { Virtuoso } from 'react-virtuoso';
import { maxChaptersPerBook } from './maxChaptersPerBook.js'; //The data object.
import {
  splitPassageEvenly,
  parseReadingList,
  validateSingleReference,
  getChapterRangeSize,
} from './bibleData.js';
import Intercom from '@intercom/messenger-js-sdk'; 

const STORAGE_KEY = 'brp_plan_v1';

// Load any previously saved plan so a refresh doesn't wipe the user's work.
const loadSavedPlan = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const SAVED = loadSavedPlan();

const slugify = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '') || 'user_readings';

const escapeXml = (value) =>
  String(value || '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;');

const App = () => {
  // Initialize Intercom
  useEffect(() => {
    Intercom({
     app_id: 'njlysz35',
    });
  }, []);

  const debounceTimeoutRef = useRef(null);
  const [passedMaxPassages, setPassedMaxPassages] = useState({});
  const [completedBooks, setCompletedBooks] = useState(new Set());
   const [numDays, setNumDays] = useState(SAVED?.numDays ?? 0);
   const [numPassagesPerDay, setNumPassagesPerDay] = useState(SAVED?.numPassagesPerDay ?? 1);
   const [inputValues, setInputValues] = useState(SAVED?.inputValues ?? []);
   const [warnings, setWarnings] = useState([]);
   const [noReadingDays, setNoReadingDays] = useState(SAVED?.noReadingDays ?? []);
   const [isDaysSet, setIsDaysSet] = useState(SAVED?.isDaysSet ?? false);
    const [isVisible, setIsVisible] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [planTitle, setPlanTitle] = useState(SAVED?.planTitle ?? 'Scripture Reading Plan');
    const [quickFillMode, setQuickFillMode] = useState('range');
    const [rangeInput, setRangeInput] = useState('');
    const [listInput, setListInput] = useState('');

  // Persist the plan so a refresh or accidental close doesn't lose work.
  useEffect(() => {
    try {
      const snapshot = {
        numDays,
        numPassagesPerDay,
        inputValues,
        noReadingDays,
        isDaysSet,
        planTitle,
      };
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
    } catch {
      // Ignore storage failures (e.g. private mode quota limits).
    }
  }, [numDays, numPassagesPerDay, inputValues, noReadingDays, isDaysSet, planTitle]);

  const validateAndTrackMaxPassages = useCallback((input) => {
    // Don't process checkbox values or empty inputs
    if (input === "on" || !input || typeof input !== 'string') return;
    
    const parsedPassage = parsePassage(input);
    if (!parsedPassage) {
      return;
    }
    
    const { book, chapterVerse } = parsedPassage;
    let bookKey = book;
    let bookData = maxChaptersPerBook[book]; 
    
    // If the book wasn't found directly, try to find a match
    if (!bookData) {
      // Try to find a match in the data object
      const matchingBook = Object.keys(maxChaptersPerBook).find(
        b => b.toLowerCase() === book.toLowerCase()
      );
      
      if (matchingBook) {
        bookKey = matchingBook;
        bookData = maxChaptersPerBook[matchingBook];
      } else {
        return;
      }
    }
    
    // Check if this is a chapter-verse format or just chapter
    const hasVerse = chapterVerse.includes(":");
    
    if (hasVerse) {
      // Parse chapter and verse
      const [chapter, verse] = chapterVerse.split(":").map(num => parseInt(num, 10));
      
      if (isNaN(chapter) || isNaN(verse)) {
        return;
      }
      
      const maxChapters = bookData.chapters;
      
      // Check if the chapter is valid
      if (chapter < 1 || chapter > maxChapters) {
        return;
      }
      
      // Get the maximum verses for this chapter
      const maxVerses = bookData.verses[chapter - 1]; // Reminder -1 because array is 0-indexed
      
      if (!maxVerses) {
        return;
      }
      
      // Update the state to track if we've reached the max chapter/verse
      setPassedMaxPassages(prev => {
        const updatedState = {
          ...prev,
          // Mark as complete when we've reached the max chapter AND max verse
          [bookKey]: (chapter === maxChapters && verse === maxVerses)
        };
        
        // You may want to track chapter completion as well
        if (!updatedState[`${bookKey}_chapter_${chapter}`]) {
          updatedState[`${bookKey}_chapter_${chapter}`] = (verse === maxVerses);
        }
        
        return updatedState;
      });
    } else {
      //chapter-only logic
      const chapter = parseInt(chapterVerse, 10);
      if (isNaN(chapter)) {
        return;
      }
      
      const maxChapters = bookData.chapters;
      
      // Update the state to track if we've reached the max chapter
      setPassedMaxPassages(prev => {
        const updatedState = {
          ...prev,
          // Mark as complete when we've reached exactly the max chapter
          [bookKey]: chapter === maxChapters
        };
        return updatedState;
      });
    }
  }, [setPassedMaxPassages]);

  const validBooks = [
    "Genesis", "Exodus", "Leviticus", "Numbers", "Deuteronomy", "Joshua", "Judges", "Ruth", 
    "1 Samuel", "2 Samuel", "1 Kings", "2 Kings", "1 Chronicles", "2 Chronicles", "Ezra", "Nehemiah", 
    "Esther", "Job", "Psalms", "Proverbs", "Ecclesiastes", "Song of Solomon", "Isaiah", "Jeremiah", 
    "Lamentations", "Ezekiel", "Daniel", "Hosea", "Joel", "Amos", "Obadiah", "Jonah", "Micah", "Nahum", 
    "Habakkuk", "Zephaniah", "Haggai", "Zechariah", "Malachi", "Matthew", "Mark", "Luke", "John", 
    "Acts", "Romans", "1 Corinthians", "2 Corinthians", "Galatians", "Ephesians", "Philippians", "Colossians", 
    "1 Thessalonians", "2 Thessalonians", "1 Timothy", "2 Timothy", "Titus", "Philemon", "Hebrews", "James", 
    "1 Peter", "2 Peter", "1 John", "2 John", "3 John", "Jude", "Revelation"
  ];

// Builds the passages for a single day, returning the "No reading today"
// placeholder when the day is empty or explicitly marked as off.
const buildDayPassages = (dayNumber) => {
  const dayIndex = dayNumber - 1;
  if (noReadingDays[dayIndex]) {
    return ['No reading today'];
  }
  const passagesForDay = inputValues
    .slice(dayNumber * numPassagesPerDay - numPassagesPerDay, dayNumber * numPassagesPerDay)
    .filter(passage => passage && passage.trim() !== '')
    .map(passage => normalizePassage(passage));
  return passagesForDay.length === 0 ? ['No reading today'] : passagesForDay;
};

// Converts a chunk of days into a single SubSplash <plan> document.
const buildPlanXml = (title, days) => {
  let xmlData = '<plan>\n';
  xmlData += `  <title>${escapeXml(title)}</title>\n`;
  days.forEach((passages, index) => {
    xmlData += `  <reading>\n    <day>${index + 1}</day>\n`;
    passages.forEach(passage => {
      xmlData += `    <passage>${escapeXml(passage)}</passage>\n`;
    });
    xmlData += `  </reading>\n`;
  });
  xmlData += '</plan>\n';
  return xmlData;
};

const downloadXmlFile = (filename, content) => {
  const blob = new Blob([content], { type: 'application/xml' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(link.href);
};

const generateAndDownloadXML = async () => {
  // Check for warnings
  if (warnings.length > 0) {
    alert("Please fix the errors in your input before generating the XML!");
    return;
  }
  
  // Check if there is data in an input, if there is no data we dont generate.
  const hasData = inputValues.some(value => value && value.trim() !== '');
  if (!hasData) {
    alert("Plan must have data entered before trying to generate an XML file");
    return;
  }

  // Safety net: re-validate every passage at download time so invalid entries
  // (e.g. a bare book name with no chapter) can never slip through.
  const invalidPassages = [];
  inputValues.forEach((value) => {
    if (!value || value.trim() === '' || value === 'on') return;
    const result = validateSingleReference(value);
    if (!result.ok) {
      invalidPassages.push(value.trim());
      return;
    }
    const span = getChapterRangeSize(value);
    if (span && span > 3) invalidPassages.push(value.trim());
  });
  if (invalidPassages.length > 0) {
    const unique = [...new Set(invalidPassages)];
    alert(`Please fix these invalid passages before downloading:\n\n${unique.join('\n')}`);
    return;
  }

  // Start loading state
  setIsGenerating(true);
  
  try {
    const allDays = [];
    for (let i = 1; i <= numDays; i++) {
      allDays.push(buildDayPassages(i));
    }

    const title = (planTitle && planTitle.trim()) || 'Scripture Reading Plan';
    const prefix = slugify(title);
    const xml = buildPlanXml(title, allDays);

    // The delay is intentional: it prevents the browser from firing the
    // download before the blob is fully written, which produced truncated files.
    await new Promise(resolve => setTimeout(resolve, 5000));

    downloadXmlFile(`${prefix}.xml`, xml);
  } catch (error) {
    console.error('Error generating XML:', error);
    alert('An error occurred while generating the XML file.');
  } finally {
    // Stop loading state
    setIsGenerating(false);
  }
};

// Function to protect the XML from excessive caps and lower case.
function normalizePassage(passage) {
  if (!passage) return '';

  let normalized = passage.trim().toLowerCase();

  // Special-case: Song of Songs we cant capitilize the of or it wont work on the app. 
  if (normalized.startsWith('song of songs')) {
    normalized = normalized.replace(/^song of songs/, 'Song of Songs');
  }

  // Capitalize the first letter of the book name at the start, and the first
  // letter of any second book name that follows a range hyphen
  // (e.g. "1 peter 5:1-2 peter 1:3" -> "1 Peter 5:1-2 Peter 1:3").
  return normalized.replace(
    /(^|-)(\s*\d*\s*)([a-z])/g,
    (full, separator, middle, letter) => separator + middle + letter.toUpperCase()
  );
}

   const handleNumDaysChange = (event) => {
    let value = event.target.value;
    
    // Allow backspacing, empty input, or a valid number as options.
    if (value === '') {
      setNumDays('');  // Allow clearing the input.
      setIsDaysSet(false);  // No days set when input is empty
    } else if (!isNaN(value)) {
      // Allow typing a valid number
      setNumDays(value);
      
      // Check if the value is within valid range
      const numValue = Number(value);
      setIsDaysSet(numValue >= 1 && numValue <= 365);
    }
  };

 const handlePassagesPerDayChange = (e) => {
    const num = parseInt(e.target.value, 10);
    setNumPassagesPerDay(num);
    setInputValues(new Array(numDays * num).fill(''));
    setWarnings([]);
  };

 const handleBlur = () => {
  // When the input loses focus, enforce the range and set it to a valid value
  let value = parseInt(numDays, 10);
  const maxDays = isLeapYear(currentYear) ? 366 : 365;
       
  if (isNaN(value) || value < 1) {
    setNumDays(1);  // Set to 1
  } else if (value > maxDays) {
    setNumDays(maxDays);  // Sets to maxDays based on leap year
  }
};

  //adjust plan max to 366 on leap years. 
  function isLeapYear(year) {
  return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
}

const currentYear = new Date().getFullYear();
const maxDays = isLeapYear(currentYear) ? 366 : 365;

  // const handleAcceptAutoFill = () => {
  //   if (!autoFillInfo) return;
    
  //   const chaptersToAdd = generateChapterSequence(autoFillInfo);
  //   let newValues = [...inputValues];
    
  //   // Find first available slots to insert the chapters
  //   let insertIndex = inputValues.findIndex((val, idx) => {
  //     // Only consider slots that aren't in a "No Reading" day
  //     const dayIndex = Math.floor(idx / numPassagesPerDay);
  //     return !val?.trim() && !noReadingDays[dayIndex];
  //   });
    
  //   if (insertIndex === -1) insertIndex = inputValues.length;
    
  //   // Calculate how many empty slots we have available
  //   let availableSlots = 0;
  //   for (let i = insertIndex; i < inputValues.length; i++) {
  //     const dayIndex = Math.floor(i / numPassagesPerDay);
  //     if (!noReadingDays[dayIndex] && !inputValues[i]?.trim()) {
  //       availableSlots++;
  //     } else if (inputValues[i]?.trim()) {
  //       // Stop counting at the first non-empty slot after empty slots
  //       break;
  //     }
  //   }
    
  //   // Limit the chapters to add based on available slots
  //   const chaptersToInsert = chaptersToAdd.slice(0, availableSlots);
    
  //   // Insert chapters into available slots
  //   let currentIndex = insertIndex;
  //   for (let i = 0; i < chaptersToInsert.length; i++) {
  //     // Find the next available slot
  //     while (currentIndex < inputValues.length) {
  //       const dayIndex = Math.floor(currentIndex / numPassagesPerDay);
  //       if (!noReadingDays[dayIndex] && !newValues[currentIndex]?.trim()) {
  //         break;
  //       }
  //       currentIndex++;
  //     }
      
  //     if (currentIndex < inputValues.length) {
  //       newValues[currentIndex] = chaptersToInsert[i];
  //       // Validate the new entry
  //       validateInput(chaptersToInsert[i], currentIndex);
  //       validateAndTrackMaxPassages(chaptersToInsert[i]);
  //       currentIndex++;
  //     }
  //   }
    
  //   setInputValues(newValues);
  //   setShowAutoFill(false);
  //   setAutoFillInfo(null);
    
  //   // If we couldn't add all chapters, inform the user
  //   if (chaptersToInsert.length < chaptersToAdd.length) {
  //     alert(`Added ${chaptersToInsert.length} out of ${chaptersToAdd.length} chapters. Not enough empty slots available.`);
  //   }
  // };

  const clearAllData = () => {
    if (window.confirm('Are you sure you want to clear ALL input data and start over?')) {
      setNumDays(0);
      setNumPassagesPerDay(1);
      setInputValues([]);
      setWarnings([]);
      setNoReadingDays([]); // Reset "No reading" days
      setPassedMaxPassages({})
      setIsDaysSet(false) //Resets passages at the max
      setPlanTitle('Scripture Reading Plan');
      setRangeInput('');
      setListInput('');
      try {
        window.localStorage.removeItem(STORAGE_KEY);
      } catch {
        // Ignore storage failures.
      }
    }
  };

  // Splits a single passage range evenly across every reading slot in the plan.
  const handleFillFromRange = () => {
    const totalSlots = numDays * numPassagesPerDay;
    if (!totalSlots) {
      alert('Set the number of days first.');
      return;
    }
    if (!rangeInput.trim()) {
      alert('Enter a passage range to split, for example "Matthew 1:1-28:20".');
      return;
    }

    const hasExistingData = inputValues.some(v => v && v.trim() !== '');
    if (hasExistingData) {
      const proceed = window.confirm(
        'This will replace all existing passages in the grid. Continue?'
      );
      if (!proceed) return;
    }

    let readings;
    try {
      readings = splitPassageEvenly(rangeInput, totalSlots);
    } catch (error) {
      alert(error.message);
      return;
    }
    const newValues = new Array(totalSlots).fill('');
    readings.forEach((reading, index) => {
      newValues[index] = reading;
    });
    setNoReadingDays([]);
    setWarnings([]);
    setInputValues(newValues);
  };

  // Fills the grid from a pasted list of references, expanding the plan if needed.
  const handleFillFromList = () => {
    const items = parseReadingList(listInput);
    if (!items.length) {
      alert('Add at least one scripture reference to the list.');
      return;
    }

    const hasExistingData = inputValues.some(v => v && v.trim() !== '');
    if (hasExistingData) {
      const proceed = window.confirm(
        'This will replace all existing passages in the grid. Continue?'
      );
      if (!proceed) return;
    }

    let totalSlots = numDays * numPassagesPerDay;

    if (items.length > totalSlots) {
      const neededDays = Math.min(maxDays, Math.ceil(items.length / numPassagesPerDay));
      const expand = window.confirm(
        `Your list has ${items.length} readings but the plan only has ${totalSlots} slot(s). Expand the plan to ${neededDays} day(s) to fit?`
      );
      if (expand) {
        totalSlots = neededDays * numPassagesPerDay;
        setNumDays(neededDays);
        setIsDaysSet(true);
      }
    }

    const newValues = new Array(totalSlots).fill('');
    const newWarnings = [];
    items.slice(0, totalSlots).forEach((reading, index) => {
      newValues[index] = reading;

      const result = validateSingleReference(reading);
      if (!result.ok) {
        newWarnings.push({
          index,
          message: `Invalid passage: "${reading}". ${result.message || ''} Make sure the book name is spelled correctly with a space after it.`.trim()
        });
      } else {
        const chapterSpan = getChapterRangeSize(reading);
        if (chapterSpan && chapterSpan > 3) {
          newWarnings.push({
            index,
            message: `Chapter range too large (${chapterSpan} chapters). Please limit to 3 chapters maximum.`
          });
        }
      }
    });
    setNoReadingDays([]);
    setWarnings(newWarnings);
    setInputValues(newValues);

    const messages = [];
    if (items.length > totalSlots) {
      messages.push(`Only the first ${totalSlots} of ${items.length} readings were added because the plan is full.`);
    }
    if (newWarnings.length > 0) {
      messages.push(`${newWarnings.length} passage(s) have validation errors. Please review the highlighted fields.`);
    }
    if (messages.length > 0) {
      alert(messages.join('\n\n'));
    }
  };

  const parsePassage = (passage) => {
    if (!passage || typeof passage !== 'string') return null;
    //Allowed regex should be, bible book name upper or lower case for the first letter. followed by chapter only, chapter and verse seperated by a : or a chapter range such as 1-3
    const regex = /^(\d?\s?[A-Za-z]+(?:\s+[A-Za-z]+)*)\s+(\d+(?:-\d+)?(?::\d+(?:-\d+)?)?)$/

    const match = passage.trim().match(regex);
    if (!match) {
      return null;
    }
    
    const book = match[1].trim();
    const chapterVerse = match[2].trim();
    
    if (!/^\d+(-\d+)?(?::\d+(?:-\d+)?)?$/.test(chapterVerse) && 
        !/^\d+\s+and\s+.+$/.test(chapterVerse)) {
      return null;
    }
    
    return {
      book: book,
      chapterVerse: chapterVerse
    };
  };
  
//useEffect to scan input values when component mounts or inputs change
  React.useEffect(() => {
    // Create a temporary copy to avoid state updates during iteration :) 
    const validInputs = inputValues.filter(val => val && val.trim() !== '');
    validInputs.forEach(value => {
      validateAndTrackMaxPassages(value);
    });
  }, [inputValues, validateAndTrackMaxPassages]); // Include all dependencies
  
  const handleInputChange = (index, e) => {
    const newValues = [...inputValues];
    const inputValue = e.target.value;
    
    newValues[index] = inputValue;
    setInputValues(newValues);
    
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    
    debounceTimeoutRef.current = setTimeout(() => {
      validateInput(inputValue, index);
      
      // Only validate non-empty inputs
      if (inputValue.trim()) {
        validateAndTrackMaxPassages(inputValue);
        
        // Check for consecutive chapters to suggest auto-fill - Only check if the current input has a value
        // Note: Auto-fill functionality is currently disabled
      }
      
      // Calculate suggestions for the next day
      if ((index + 1) % numPassagesPerDay === 0) {
        suggestNextDayPassages(index, newValues);
      }
    }, 100);
  };
  
  React.useEffect(() => {
    // Initialize with an empty object
    setPassedMaxPassages({});
    
    // Initial scan of any pre-existing values
    if (inputValues.length > 0) {
      inputValues.forEach(value => {
        if (value && value.trim() !== '') {
          validateAndTrackMaxPassages(value);
        }
      });
    }
  }, [inputValues, validateAndTrackMaxPassages]); // Include all dependencies

// Auto-fill functionality is currently disabled
    
// Auto-fill functionality is currently disabled
  
// Auto-fill functionality is currently disabled
  
const getSuggestedPassagePlaceholder = (index) => {
  if (index >= numDays * numPassagesPerDay) return "";
  
  // Don't suggest if it already has a value
  if (inputValues[index]?.trim()) return "";
  
  const currentDayIndex = Math.floor(index / numPassagesPerDay);
  const passageInDayIndex = index % numPassagesPerDay;
  
  // If it's a no reading day, don't suggest anything
  if (noReadingDays[currentDayIndex]) return "";
  
  // Look for the last valid passage of the same type
  let previousPassage = null;
  let day = currentDayIndex;
  
  // Find the most recent valid passage and count actual reading days
  while (day >= 0 && !previousPassage) {
    // Skip no-reading days in our count
    if (noReadingDays[day]) {
      day--;
      continue;
    }
    
    const dayStartIndex = day * numPassagesPerDay;
    const passageIndex = dayStartIndex + passageInDayIndex;
    
    if (passageIndex < index && inputValues[passageIndex]?.trim()) {
      previousPassage = parsePassage(inputValues[passageIndex]);
      break;
    }
    
    day--;
  }
  
  // If no previous passage was found, return default
  if (!previousPassage) {
    return `Passage ${index + 1}`;
  }
  
  const { book, chapterVerse } = previousPassage;

  // Add safety check - if normalizeBook returns null, use a default value
  const normalizedBookName = normalizeBook(book);
  if (!normalizedBookName) {
    return `Passage ${index + 1}`;
  }

  // Find the correct book key 
  const bookKey = Object.keys(maxChaptersPerBook).find(
    key => key.toLowerCase() === normalizedBookName.toLowerCase()
  );
    
  if (!bookKey) {
    return `Passage ${index + 1}`;
  }
  
  // Check if we're dealing with verses (format includes ":")
  const hasVerses = chapterVerse.includes(":");
  
  // Check if we're dealing with chapter ranges (format includes "-" but no ":")
  const isChapterRange = chapterVerse.includes("-") && !hasVerses;

  if (hasVerses) {
    // Extract chapter and verse information (handling ranges with "-")
    let chapter, lastVerse, firstVerse;
    
    if (chapterVerse.includes("-")) {
      // Handle verse ranges (e.g., "John 1:1-10")
      const [chapterPart, versePart] = chapterVerse.split(":");
      chapter = parseInt(chapterPart, 10);
      
      const [start, end] = versePart.split("-").map(num => parseInt(num, 10));
      firstVerse = start;
      lastVerse = end; // We'll use the end of the range for suggestions
    } else {
      // Handle single verse (e.g., "1:1")
      const [chapterPart, versePart] = chapterVerse.split(":");
      chapter = parseInt(chapterPart, 10);
      lastVerse = parseInt(versePart, 10);
      firstVerse = lastVerse;
    }
    
    const maxVerses = maxChaptersPerBook[bookKey].verses[chapter - 1]; // -1 because array is 0-indexed
    
    // Check for break points in this chapter
    const hasBreaks = maxChaptersPerBook[bookKey].breaks && 
                     maxChaptersPerBook[bookKey].breaks[chapter] && 
                     maxChaptersPerBook[bookKey].breaks[chapter].length > 0;
    
    // If we're not at the end of the chapter yet
    if (lastVerse < maxVerses) {
      //If the user input just a single verse, suggest only the next verse
      if (firstVerse === lastVerse) {
        return `${book} ${chapter}:${lastVerse + 1}`;
      } 
      //User input a range of verses
      else {
        // Check if there are breaks in this chapter
        if (hasBreaks) {
          // Find the next break point after our current verse
          const nextBreaks = maxChaptersPerBook[bookKey].breaks[chapter].filter(v => v > lastVerse);
          
          if (nextBreaks.length > 0) {
            // Suggest up to the next break point
            const nextBreak = nextBreaks[0];
            return `${book} ${chapter}:${lastVerse + 1}-${nextBreak}`;
          } else {
            // No more breaks, suggest to the end of the chapter
            return `${book} ${chapter}:${lastVerse + 1}-${maxVerses}`;
          }
        } 
        // No breaks defined for this chapter, suggest 50% of verses
        else {
          // Calculate 50% of the remaining verses in the chapter
          const remainingVerses = maxVerses - lastVerse;
          const halfRemainingVerses = Math.ceil(remainingVerses / 2);
          
          // If there are very few verses left (less than 5), just suggest them all
          if (remainingVerses <= 5) {
            return `${book} ${chapter}:${lastVerse + 1}-${maxVerses}`;
          } else {
            const nextEnd = lastVerse + halfRemainingVerses;
            return `${book} ${chapter}:${lastVerse + 1}-${nextEnd}`;
          }
        }
      }
    } 
    // If we're at the end of the chapter, move to the next chapter
    else if (chapter < maxChaptersPerBook[bookKey].chapters) {
      const nextChapter = chapter + 1;
      const nextChapterMaxVerses = maxChaptersPerBook[bookKey].verses[nextChapter - 1];
      
      // Check for break points in the next chapter
      const nextChapterHasBreaks = maxChaptersPerBook[bookKey].breaks && 
                                  maxChaptersPerBook[bookKey].breaks[nextChapter] && 
                                  maxChaptersPerBook[bookKey].breaks[nextChapter].length > 0;
      
      // If the user input just a single verse, suggest only the first verse of next chapter
      if (firstVerse === lastVerse) {
        return `${book} ${nextChapter}:1`;
      } 
      // User input a range of verses
      else {
        // Check if there are breaks in the next chapter
        if (nextChapterHasBreaks && maxChaptersPerBook[bookKey].breaks[nextChapter].length > 0) {
          const firstBreak = maxChaptersPerBook[bookKey].breaks[nextChapter][0];
          return `${book} ${nextChapter}:1-${firstBreak}`;
        } 
        // No breaks, suggest 50% of the next chapter
        else {
          const halfNextChapterVerses = Math.ceil(nextChapterMaxVerses / 2);
          return `${book} ${nextChapter}:1-${halfNextChapterVerses}`;
        }
      }
    } else {
      // We've reached the end of the book - DON'T call markBookAsCompleted here!
      const currentBookWeight = maxChaptersPerBook[bookKey].weight;
      
      // Create a temporary set that includes the completed book
      const tempCompletedBooks = new Set(completedBooks);
      tempCompletedBooks.add(bookKey);
      
      // Get next suggestion using the temporary set
      const nextSuggestion = getNextSuggestion(currentBookWeight, tempCompletedBooks);
      
      // Handle the object return from getNextSuggestion
      if (nextSuggestion && typeof nextSuggestion === 'object' && nextSuggestion.book) {
        const nextBookName = nextSuggestion.book;
        // Schedule marking the book as completed for the next render cycle
        setTimeout(() => {
          markBookAsCompleted(bookKey);
        }, 0);
        
        return `${nextBookName} 1`;
      }
      
      // Last resort - try to get any uncompleted book
      const uncompletedBooks = Object.keys(maxChaptersPerBook).filter(book => !tempCompletedBooks.has(book));
      if (uncompletedBooks.length > 0) {
        const nextBook = uncompletedBooks[0];
        // Schedule marking the book as completed for the next render cycle
        setTimeout(() => {
          markBookAsCompleted(bookKey);
        }, 0);
        
        return `${nextBook} 1`;
      }
      
      return `Passage ${index + 1}`;
    }
  } else if (isChapterRange) {
    // Handle chapter ranges like "Genesis 1-3"
    const [startChapter, endChapter] = chapterVerse.split("-").map(num => parseInt(num, 10));
    const rangeSize = endChapter - startChapter + 1;
    const maxChapters = maxChaptersPerBook[bookKey].chapters;

    // If we haven't reached the end of the book, suggest the next range
    if (endChapter < maxChapters) {
      const nextStartChapter = endChapter + 1;
      let nextEndChapter = nextStartChapter + rangeSize - 1; // Maintain the same range size
      
      // Don't exceed the maximum chapters in the book
      if (nextEndChapter > maxChapters) {
        nextEndChapter = maxChapters;
      }
      
      // If it's just one chapter, don't use range format
      if (nextStartChapter === nextEndChapter) {
        return `${book} ${nextStartChapter}`;
      } else {
        return `${book} ${nextStartChapter}-${nextEndChapter}`;
      }
    } else {
      // We've reached the end of the book
      const currentBookWeight = maxChaptersPerBook[bookKey].weight;
      
      // Create a temporary set that includes the completed book
      const tempCompletedBooks = new Set(completedBooks);
      tempCompletedBooks.add(bookKey);
      
      // Get next suggestion using the temporary set
      const nextSuggestion = getNextSuggestion(currentBookWeight, tempCompletedBooks);
      
      // Handle the object return from getNextSuggestion
      if (nextSuggestion && typeof nextSuggestion === 'object' && nextSuggestion.book) {
        const nextBookName = nextSuggestion.book;
        // Schedule marking the book as completed for the next render cycle
        setTimeout(() => {
          markBookAsCompleted(bookKey);
        }, 0);
        
        return `${nextBookName} 1`;
      }
      
      // Last resort - try to get any uncompleted book
      const uncompletedBooks = Object.keys(maxChaptersPerBook).filter(book => !tempCompletedBooks.has(book));
      if (uncompletedBooks.length > 0) {
        const nextBook = uncompletedBooks[0];
        // Schedule marking the book as completed for the next render cycle
        setTimeout(() => {
          markBookAsCompleted(bookKey);
        }, 0);
        
        return `${nextBook} 1`;
      }
      
      return `Passage ${index + 1}`;
    }
  } else {
    // Handle single chapters
    const chapter = parseInt(chapterVerse, 10);
    const maxChapters = maxChaptersPerBook[bookKey].chapters;
    
    if (chapter < maxChapters) {
      return `${book} ${chapter + 1}`;
    } else {
      // Book completed
      const currentBookWeight = maxChaptersPerBook[bookKey].weight;
      const tempCompletedBooks = new Set(completedBooks);
      tempCompletedBooks.add(bookKey);
      
      const nextSuggestion = getNextSuggestion(currentBookWeight, tempCompletedBooks);
      
      // Handle the return value properly
      if (nextSuggestion && typeof nextSuggestion === 'object' && nextSuggestion.book) {
        // Schedule marking the book as completed for the next render cycle
        setTimeout(() => {
          markBookAsCompleted(bookKey);
        }, 0);
        
        return `${nextSuggestion.book} 1`;
      } else if (typeof nextSuggestion === 'string' && nextSuggestion.trim()) {
        return nextSuggestion;
      } else {
        return `Passage ${index + 1}`;
      }
    }
  }
};

const suggestNextDayPassages = (index, newValues) => {
  try {
    const currentDayIndex = Math.floor(index / numPassagesPerDay);
    const nextDayIndex = currentDayIndex + 1;
    
    // If next day is beyond our range or is a no-reading day, don't suggest
    if (nextDayIndex >= numDays || noReadingDays[nextDayIndex]) return;
    
    // Get the current day's passages
    const currentDayStartIndex = currentDayIndex * numPassagesPerDay;
    const currentDayPassages = [];
    
    for (let i = 0; i < numPassagesPerDay; i++) {
      const passageIndex = currentDayStartIndex + i;
      if (newValues[passageIndex]?.trim()) {
        const passage = parsePassage(newValues[passageIndex]);
        if (passage && passage.book && passage.chapterVerse) {
          currentDayPassages.push(passage);
        }
      }
    }
    
    const nextDayStartIndex = nextDayIndex * numPassagesPerDay;
    
    for (let i = 0; i < numPassagesPerDay; i++) {
      const nextIndex = nextDayStartIndex + i;
      
      if (i < currentDayPassages.length) {
        const { book, chapterVerse } = currentDayPassages[i];
        
        // Safety check - ensure book and chapterVerse are present
        if (!book || !chapterVerse) continue;
        
        // safety check for chapterVerse format
        if (!chapterVerse.includes(":")) {
          // Handle chapter-only format
          try {
            const chapter = parseInt(chapterVerse, 10);
            if (isNaN(chapter)) continue;
            
            // Safety check for normalizeBook result
            const normalizedBookName = normalizeBook(book);
            if (!normalizedBookName) continue;
            
            // Find the correct book key with safety check
            const bookKey = Object.keys(maxChaptersPerBook).find(
              key => key.toLowerCase() === normalizedBookName.toLowerCase()
            );
            
            // Only proceed if we found a valid book key
            if (bookKey && chapter < maxChaptersPerBook[bookKey].chapters) {
              // Update the suggestion state for this field
              updateSuggestionForField(nextIndex);
            }
          } catch (error) {
            // Skip if there's an error parsing
            continue;
          }
        } else {
          try {
            // Split by colon and parse as integer to find verses
            const [chapterPart] = chapterVerse.split(":");
            const chapter = parseInt(chapterPart, 10);
            
            if (isNaN(chapter)) continue;
            
            // Safety check for normalizeBook result
            const normalizedBookName = normalizeBook(book);
            if (!normalizedBookName) continue;
            
            // Find the correct book key with safety check
            const bookKey = Object.keys(maxChaptersPerBook).find(
              key => key.toLowerCase() === normalizedBookName.toLowerCase()
            );
            
            // Only proceed if we found a valid book key
            if (bookKey && chapter < maxChaptersPerBook[bookKey].chapters) {
              // Update the suggestion state for this field
              updateSuggestionForField(nextIndex);
            }
          } catch (error) {
            // Skip if there's an error parsing
            continue;
          }
        }
      }
    }
  } catch (error) {
    // Global try/catch to prevent any crash
  }
};

const weightSuggestions = {
  1: [1, 2, 4, 5, 3, 6, 7, 8, 9, 12],
  2: [2, 1, 4, 5, 3, 6, 7, 8, 9, 12],
  3: [3, 6, 12, 7, 8, 9, 1, 2, 4, 5],
  4: [4, 5, 2, 6, 3, 7, 8, 9, 12],
  5: [5, 2, 4, 1, 3, 6, 7, 8, 9, 12],
  6: [6, 7, 8, 9, 12, 3, 1, 2, 5, 4],
  7: [7, 8, 6, 9, 12, 3, 1, 2, 4, 5],
  8: [8, 9, 6, 7, 12, 3, 1, 2, 4, 5],
  9: [9, 6, 8, 7, 12, 3, 1, 2, 4, 5],
  12: [12, 6, 5, 7, 8, 9, 1, 2, 3, 4]
};

const getBooksByWeight = (weight) => {
  return Object.keys(maxChaptersPerBook).filter(book => 
    maxChaptersPerBook[book].weight === weight
  );
};

const getNextSuggestion = (fromWeight, completedBooksSet = completedBooks) => {
  const suggestions = weightSuggestions[fromWeight] || [];

  for (const suggestedWeight of suggestions) {
    const booksInWeight = getBooksByWeight(suggestedWeight);
    const availableBooks = booksInWeight.filter(book => !completedBooksSet.has(book));

    if (availableBooks.length > 0) {
      return {
        book: availableBooks[0],
        weight: suggestedWeight
      };
    }
  }

  return null; // No more suggestions available
};

  // helper function for tracking what books have been suggested so far. updates the books.js list as finished. 
const markBookAsCompleted = (bookName) => {
  if (!completedBooks.has(bookName)) {
    const newCompletedBooks = new Set(completedBooks);
    newCompletedBooks.add(bookName);
    setCompletedBooks(newCompletedBooks);
    
    // Book completion tracking is handled elsewhere
  }
};

  const isPassagesFilled = () => {
    return inputValues.some((value) => typeof value === 'string' && value.trim() !== '');
  };

  const virtuosoRef = useRef(null);
  
  const handleKeyDown = (index, e) => {
    if (e.key === 'Tab' && !e.shiftKey) {
      const currentValue = inputValues[index];
      const suggestion = getSuggestedPassagePlaceholder(index);
      
      // Only handle Tab if there's no value and we have a valid suggestion :) 
      if (!currentValue && suggestion && suggestion.indexOf('Passage') === -1) {
        e.preventDefault(); // Prevent default tab behavior, also tab is disabled on the HTML elemnts as well. 
        
        // Update the current field with the suggestion
        const newValues = [...inputValues];
        newValues[index] = suggestion;
        setInputValues(newValues);
        
        // Validate the new input
        validateInput(suggestion, index);
        
        // check if we've reached max chapter
        validateAndTrackMaxPassages(suggestion);
        
        // Calculate the next index
        const nextIndex = index + 1;
        if (nextIndex < numDays * numPassagesPerDay) {
          // Scroll to the next item in the virtualized list
          virtuosoRef.current?.scrollToIndex({
            index: nextIndex,
            behavior: 'smooth'
          });
          
          setTimeout(() => {
            // Focus the next input field
            const nextInput = document.querySelector(`input[type="text"][placeholder*="Day ${Math.floor(nextIndex / numPassagesPerDay) + 1} - Passage ${nextIndex % numPassagesPerDay + 1}"]`);
            if (nextInput) {
              nextInput.focus();
              updateSuggestionForField(nextIndex);
            }
          }, 10);
        }
      }
    }
  };

  const normalizeBook = (bookName) => {
    const book = bookName.toLowerCase().trim();
    
    // First try direct match
    const fullMatch = validBooks.find(b => b.toLowerCase() === book);
    if (fullMatch) {
      return fullMatch; // Return the properly capitalized version
    }
    
    // Then try abbreviations
    const abbrevMatch = Object.keys(maxChaptersPerBook).find(
      b => b.toLowerCase() === book
    );
    
    if (abbrevMatch) {
      return abbrevMatch;
    }
    
    return null;
  };

//warnings for the user
 const clearWarning = (index) => {
   setWarnings((prevWarnings) => prevWarnings.filter((warning) => warning.index !== index));
 };

 const addWarning = (index, message) => {
   setWarnings((prevWarnings) => {
     const others = prevWarnings.filter((warning) => warning.index !== index);
     return [...others, { index, message }];
   });
 };

 const validateInput = (value, index) => {
  if (!value || value.trim() === '') {
    clearWarning(index);
    return;
  }

  const result = validateSingleReference(value);
  if (!result.ok) {
    addWarning(
      index,
      `Invalid passage: "${value}". ${result.message || ''} Make sure the book name is spelled correctly with a space after it.`.trim()
    );
    return;
  }

  // Preserve the SubSplash rule that a single field holds at most 3 chapters.
  const chapterSpan = getChapterRangeSize(value);
  if (chapterSpan && chapterSpan > 3) {
    addWarning(index, `Chapter range too large (${chapterSpan} chapters). Please limit to 3 chapters maximum.`);
    return;
  }

  clearWarning(index);
};

  const handleNoReadingChange = (dayIndex) => {
    const newNoReadingDays = [...noReadingDays];
    newNoReadingDays[dayIndex] = !newNoReadingDays[dayIndex];
    setNoReadingDays(newNoReadingDays);
    
    // If setting to "No Reading", clear all input fields for that day
    if (newNoReadingDays[dayIndex]) {
      const newInputValues = [...inputValues];
      // Clear all passages for this day
      for (let i = 0; i < numPassagesPerDay; i++) {
        const index = (dayIndex * numPassagesPerDay) + i;
        newInputValues[index] = '';
      }
      setInputValues(newInputValues);
      
      // Also clear any warnings for this day's passages
      setWarnings(prevWarnings => 
        prevWarnings.filter(warning => 
          Math.floor(warning.index / numPassagesPerDay) !== dayIndex
        )
      );
    }
  };

  const updateSuggestionForField = (index) => {
    // Suggestion tracking is handled in the input field
  };

  const renderInput = (index) => {
    const dayIndex = Math.floor(index / numPassagesPerDay);
    const passageIndex = index % numPassagesPerDay;
    const isFirstPassageOfDay = passageIndex === 0;
    const isLastPassageOfDay = passageIndex === numPassagesPerDay - 1;
    const suggestion = getSuggestedPassagePlaceholder(index);
    
    if (noReadingDays[dayIndex] && !isFirstPassageOfDay) {
      return null;
    }
    
    return (
      <div key={index} className="input-wrapper">
        {isFirstPassageOfDay && (
          <h4 className="day-title">Day {dayIndex + 1}</h4>
        )}
        {!noReadingDays[dayIndex] && (
          <div className="input-suggestion-wrapper">
            <input
              type="text"
              value={inputValues[index] || ''}
              onChange={(e) => handleInputChange(index, e)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onFocus={() => updateSuggestionForField(index)}
              className={warnings.some((w) => w.index === index) ? 'input-error' : ''}
            />
            
            {!inputValues[index] && suggestion && suggestion.indexOf('Passage') === -1 && (
              <div className="suggestion-text">Press Tab to use: {suggestion}</div>
            )}
            {warnings.some((w) => w.index === index) && (
              <span className="warning-message">
                {warnings.find((w) => w.index === index).message}
              </span>
            )}
          </div>
        )}
        {(isFirstPassageOfDay && noReadingDays[dayIndex]) || 
         (isLastPassageOfDay && !noReadingDays[dayIndex]) ? (
          <div className="no-reading-wrapper">
            <label className="no-reading-label">
              <input
                type="checkbox"
                checked={noReadingDays[dayIndex]}
                onChange={() => handleNoReadingChange(dayIndex)}
                className="check"
                tabIndex={-1}
              />
              Check for no reading
            </label>
          </div>
        ) : null}
      </div>
    );
  };
   
  const itemRenderer = (index) => {
    const dayIndex = Math.floor(index / numPassagesPerDay);
    const passageIndex = index % numPassagesPerDay;
    
    // For the first passage of each day, always render to show the checkbox
    if (passageIndex === 0) {
      return renderInput(index);
    }
    
    // For subsequent passages, only render if "No Reading" is not checked
    if (!noReadingDays[dayIndex]) {
      return renderInput(index);
    }
    
    // Return null for passages that should be hidden
    return null;
  };


const CustomTooltip = ({ message, isVisible }) => (
  <div className={`tooltip ${isVisible ? 'visible' : ''}`}>
    {message}
  </div>
);
    
  // const generateChapterSequence = (autoFillInfo) => {
  //   const { book, startChapter, endChapter, isAscending } = autoFillInfo;
  //   const chapters = [];
    
  //   if (isAscending) {
  //     // Ascending order (e.g., 3, 4, 5, ...)
  //     for (let chapter = startChapter; chapter <= endChapter; chapter++) {
  //       chapters.push(`${book} ${chapter}`);
  //     }
  //   } else {
  //     // Descending order (e.g., 3, 2, 1)
  //     for (let chapter = startChapter; chapter >= endChapter; chapter--) {
  //       chapters.push(`${book} ${chapter}`);
  //     }
  //   }
    
  //   return chapters;
  // };
  // const AutoFillPopup = ({ info, onAccept, onCancel }) => {
  //   if (!info) return null;
    
  //   const { book, startChapter, endChapter, isAscending, chapterCount } = info;
  //   const direction = isAscending ? "ascending" : "descending";
  //   const remainingCount = chapterCount || 
  //     (isAscending ? endChapter - startChapter + 1 : startChapter - endChapter + 1);
    
  //   return (
  //     <div className="auto-fill-popup">
  //       <div className="popup-content">
  //         <h3>Auto-fill Chapters</h3>
  //         <p>
  //           Would you like to auto-fill the remaining {remainingCount} chapters of {book}?
  //         </p>
  //         <p>
  //           This will add chapters {isAscending ? 
  //             `${startChapter} through ${endChapter} (ascending)` : 
  //             `${startChapter} down to ${endChapter} (descending)`
  //           }
  //         </p>
  //         <div className="popup-buttons">
  //           <button onClick={onCancel}>Cancel</button>
  //           <button onClick={onAccept}>Auto-fill</button>
  //         </div>
  //       </div>
  //     </div>
  //   );
  // };
  return (
    <div className="container">
    <nav className="top-nav">
      <div className="top-nav-logo">
        <img src="https://cdn.prod.website-files.com/627d5cb7a65390b3f8dcbc63/64d1544ddd5b1fdaa7a3635d_Subsplash_Logo_White.svg" alt="Subsplash" style={{filter: 'brightness(0)'}} />
      </div>
      <div className="top-nav-divider" />
      <span className="top-nav-title">Bible Reading Plan Builder</span>
    </nav>
    <div className="wrapper-container">
    <div className="app-container">
      <Books 
        passedMaxPassages={passedMaxPassages}
        validateAndTrackMaxPassages={validateAndTrackMaxPassages}
      />
      </div>
<div className="app-container">
        <div className="form-container">
          <h1>Plan Configuration</h1>
            <label htmlFor="planTitle" className="input-label">
              Plan title (added as the &lt;title&gt; in the exported file).
            </label>
            <input
              type="text"
              id="planTitle"
              className="input-field"
              value={planTitle}
              onChange={(e) => setPlanTitle(e.target.value)}
              placeholder="Scripture Reading Plan"
            />
            <label htmlFor="numDays" className="input-label">
              Select the number of days you want the plan to run for first. (1-{maxDays})
            </label>
           <div 
              onMouseEnter={isPassagesFilled() ? () => setIsVisible(true) : undefined}
              onMouseLeave={isPassagesFilled() ? () => setIsVisible(false) : undefined}
            >
              <input
                type="number"
                id="numDays"
                className="input-field"
                min="1"
                max={maxDays}
                onChange={handleNumDaysChange}
                onBlur={handleBlur}
                value={numDays === 0 ? '' : numDays}
                placeholder="Enter the number of days for the plan duration."
                disabled={isPassagesFilled()}
              />
              {isPassagesFilled() && (
                <CustomTooltip
                  message="Once data is entered, you cannot change the plan length or number of passages. To start over, click the Reset Plan button below."
                  isVisible={isVisible}
                />
              )}
            </div>
              <div>
            <label htmlFor="numPassagesPerDay" className="input-label">
              Select number of passages per day (1-15) Once data is entered on the plan you won't be able to change this. 
            </label>
             <div 
              onMouseEnter={isPassagesFilled() ? () => setIsVisible(true) : undefined}
              onMouseLeave={isPassagesFilled() ? () => setIsVisible(false) : undefined}
            >
            <select
              id="numPassagesPerDay"
              className="input-field"
              value={numPassagesPerDay}
              onChange={handlePassagesPerDayChange}
              disabled={isPassagesFilled()} 
            >
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15].map((num) => (
                <option key={num} value={num}>
                  {num} Passage{num > 1 ? 's' : ''}
                </option>
              ))}
            </select>
            </div>
          </div>
          {isDaysSet && (
          <div className="quick-fill">
            <div className="quick-fill-header">
              <strong>Quick fill</strong>
              <span className="quick-fill-hint">Auto-populate the grid below, then fine-tune any day. Choose one option:</span>
            </div>
            <div className="quick-fill-tabs">
              <button
                type="button"
                tabIndex="-1"
                className={`quick-fill-tab ${quickFillMode === 'range' ? 'active' : ''}`}
                onClick={() => setQuickFillMode('range')}
              >
                Split a passage range
              </button>
              <span className="quick-fill-divider">or</span>
              <button
                type="button"
                tabIndex="-1"
                className={`quick-fill-tab ${quickFillMode === 'list' ? 'active' : ''}`}
                onClick={() => setQuickFillMode('list')}
              >
                Paste a list
              </button>
            </div>

            {quickFillMode === 'range' ? (
              <div className="quick-fill-body">
                <label htmlFor="rangeInput" className="input-label">
                  Enter a passage range to split evenly across all {numDays * numPassagesPerDay} slot(s).
                </label>
                <input
                  type="text"
                  id="rangeInput"
                  className="input-field"
                  value={rangeInput}
                  onChange={(e) => setRangeInput(e.target.value)}
                  placeholder="e.g. Matthew 1:1-28:20, John 1-3, 1 Peter, or 1 Peter - 2 Peter"
                />
                <button type="button" tabIndex="-1" className="btn btn-primary" onClick={handleFillFromRange}>
                  Fill grid from range
                </button>
              </div>
            ) : (
              <div className="quick-fill-body">
                <label htmlFor="listInput" className="input-label">
                  Paste one reference per line (or separated by commas / semicolons).
                </label>
                <textarea
                  id="listInput"
                  className="input-field quick-fill-textarea"
                  value={listInput}
                  onChange={(e) => setListInput(e.target.value)}
                  placeholder={"Matthew 1:1-7\nMatthew 1:8-13\nMatthew 1:14-19"}
                />
                <button type="button" tabIndex="-1" className="btn btn-primary" onClick={handleFillFromList}>
                  Fill grid from list
                </button>
              </div>
            )}
          </div>
          )}
          {isDaysSet && (
          <div className="inputs-container">
          <Virtuoso
            ref={virtuosoRef}
            style={{ height: '900px', backgroundColor: '#f5f6f7', borderRadius: '8px'}}
            totalCount={numDays * numPassagesPerDay}
            itemContent={itemRenderer}
          />
        </div>
          )}
           {!isDaysSet && (
            <div className="placeholder-message">
              Please select the number of days for your plan first.  Once entered you can start filling passages in. 
            </div>
          )}
          <div className="button-container">
            <button tabIndex="-1" onClick={clearAllData} className="btn btn-light btn-md">
              Reset Plan
            </button>
            <button 
      onClick={generateAndDownloadXML}
      disabled={isGenerating}
      className={`btn btn-primary btn-md${isGenerating ? ' btn-loading' : ''}`}
    >
      {isGenerating ? 'Creating…' : 'Download plan'}
    </button>
          </div>
        </div>
      </div>
    </div>
      <div className="container-footer">
      <footer className="subsplash-footer">
        <div className="footer-content">
          <div className="footer-brand">
            <div className="footer-logo">
              <img src="https://cdn.prod.website-files.com/627d5cb7a65390b3f8dcbc63/64d1544ddd5b1fdaa7a3635d_Subsplash_Logo_White.svg" alt="Subsplash Logo" />
            </div>
            <div className="footer-copyright">
              © {new Date().getFullYear()} Subsplash.
            </div>
          </div>
          <div className="footer-links">
            <div className="footer-col">
              <div className="footer-col-title">Resources</div>
              <a href="https://subsplash.canny.io/changelog">Platform Updates</a>
              <a href="https://www.subsplash.com/blog">Blog</a>
              <a href="https://www.subsplash.com/resources/webinars">Webinars</a>
              <a href="https://status.subsplash.com/">System Status</a>
            </div>
            <div className="footer-col">
              <div className="footer-col-title">Support</div>
              <a href="https://dashboard.subsplash.com/">Dashboard</a>
              <a href="https://academy.subsplash.com/">Academy</a>
              <a href="https://support.subsplash.com/">Knowledge Center</a>
              <a href="https://subsplash.canny.io/">Feature Requests</a>
            </div>
            <div className="footer-col">
              <div className="footer-col-title">Company</div>
              <a href="https://www.subsplash.com/company">About</a>
              <a href="https://www.subsplash.com/legal">Terms</a>
              <a href="https://www.subsplash.com/privacy">Privacy</a>
              <a href="https://trust.subsplash.com/">Security</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
    </div>
  );
};

export default App;